import React from 'react';
import { LucideIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SFIconProps {
  icon: LucideIcon | undefined | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'primary' | 'muted' | 'white';
  className?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
}

const sizeMap = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8'
};

const variantMap = {
  default: 'text-current',
  primary: 'text-primary',
  muted: 'text-muted-foreground',
  white: 'text-white'
};

/**
 * SFIcon - Safe icon wrapper that prevents React Error #130
 *
 * CRITICAL FIX: This component now handles undefined/null icons gracefully.
 * Before: Rendering undefined as <Icon /> caused React Error #130.
 * After: Returns a fallback icon (AlertCircle) with dev warning.
 *
 * @see REACT_130_310_SWEEP.md for full explanation
 */
export const SFIcon: React.FC<SFIconProps> = ({
  icon: Icon,
  size = 'md',
  variant = 'default',
  className,
  'aria-hidden': ariaHidden,
  'aria-label': ariaLabel,
}) => {
  // CRITICAL: Prevent React Error #130 by checking if Icon is defined
  // This was the root cause of the persistent /dashboard/wallet crash
  if (!Icon) {
    if (import.meta.env?.DEV) {
      console.warn('[SFIcon] Received undefined icon. Check sfIcons reference.');
    }
    // Render fallback icon instead of crashing
    return (
      <AlertCircle
        className={cn(
          sizeMap[size],
          'text-orange-500', // Warning color to make it visible in dev
          'stroke-2',
          className
        )}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel || 'Missing icon'}
      />
    );
  }

  return (
    <Icon
      className={cn(
        sizeMap[size],
        variantMap[variant],
        'stroke-2', // SF Symbols consistent stroke width
        className
      )}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
    />
  );
};